import React from 'react'
import { useAppStore } from '../lib/store'
import { Settings, Plus, Search, Filter, Edit, Trash2, Eye, DollarSign, Clock, Users, Star, ToggleLeft, ToggleRight } from 'lucide-react'
import { toast } from '../lib/toast'
import ServiceModal from './ServiceModal'

interface Service {
  id: string
  name: string
  category: string
  base_price: number
  duration_hours: number
  complexity_level: number
  requires_team: boolean
  min_team_size: number
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
  popular?: boolean
  price_per_room?: number
}

export default function ServicesManagement() {
  const { user } = useAppStore()
  const [services, setServices] = React.useState<Service[]>([])
  const [loading, setLoading] = React.useState(true)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [categoryFilter, setCategoryFilter] = React.useState('all')
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [editingService, setEditingService] = React.useState<Service | null>(null)
  const [modalMode, setModalMode] = React.useState<'view' | 'edit' | 'create'>('create')

  React.useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = () => {
    // Mock services data
    const mockServices: Service[] = [
      {
        id: '1',
        name: 'Standard House Cleaning',
        category: 'cleaning',
        base_price: 150,
        duration_hours: 3,
        complexity_level: 2,
        requires_team: false,
        min_team_size: 1,
        description: 'Complete house cleaning including all rooms',
        is_active: true,
        created_at: '2024-01-15T10:30:00Z',
        updated_at: '2024-03-10T14:22:00Z',
        popular: true
      },
      {
        id: '2',
        name: 'Deep Kitchen Cleaning',
        category: 'cleaning',
        base_price: 200,
        duration_hours: 4,
        complexity_level: 3,
        requires_team: true,
        min_team_size: 2,
        description: 'Intensive kitchen cleaning with degreasing',
        is_active: true,
        created_at: '2024-01-20T11:15:00Z',
        updated_at: '2024-03-05T09:30:00Z'
      },
      {
        id: '3',
        name: 'Office Cleaning',
        category: 'commercial',
        base_price: 300,
        duration_hours: 5,
        complexity_level: 2,
        requires_team: true,
        min_team_size: 3,
        description: 'Commercial office space cleaning',
        is_active: true,
        created_at: '2024-02-01T16:40:00Z',
        updated_at: '2024-03-12T11:20:00Z'
      },
      {
        id: '4',
        name: 'Carpet Shampooing',
        category: 'specialized',
        base_price: 120,
        duration_hours: 2,
        complexity_level: 1,
        requires_team: false,
        min_team_size: 1,
        description: 'Professional carpet cleaning and shampooing',
        is_active: false,
        created_at: '2024-02-10T14:22:00Z',
        updated_at: '2024-03-01T08:15:00Z'
      }
    ]
    setServices(mockServices)
    setLoading(false)
  }

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const handleToggleService = (serviceId: string) => {
    setServices(services.map(s => 
      s.id === serviceId ? { ...s, is_active: !s.is_active } : s
    ))
    toast.success('Service status updated')
  }

  const handleDeleteService = (serviceId: string) => {
    if (confirm('Are you sure you want to delete this service?')) {
      setServices(services.filter(s => s.id !== serviceId))
      toast.success('Service deleted successfully')
    }
  }

  const handleCreateService = () => {
    setEditingService(null)
    setModalMode('create')
    setShowAddModal(true)
  }

  const handleEditService = (service: Service) => {
    setEditingService(service)
    setModalMode('edit')
    setShowAddModal(true)
  }

  const handleViewService = (service: Service) => {
    setEditingService(service)
    setModalMode('view')
    setShowAddModal(true)
  }

  const handleSaveService = (service: Service) => {
    if (modalMode === 'create') {
      setServices([...services, service])
    } else {
      setServices(services.map(s => s.id === service.id ? service : s))
    }
  }

  const handleDeleteServiceFromModal = (serviceId: string) => {
    setServices(services.filter(s => s.id !== serviceId))
    toast.success('Service deleted successfully')
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cleaning': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
      case 'commercial': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      case 'specialized': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
    }
  }

  const getComplexityStars = (level: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < level ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
      />
    ))
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600 dark:text-dark-text-primary">Loading services...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Settings className="w-6 h-6 text-green-600" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">Services Management</h1>
            </div>
            
            <button
              onClick={() => window.history.back()}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-lg transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Categories</option>
                <option value="cleaning">Cleaning</option>
                <option value="commercial">Commercial</option>
                <option value="specialized">Specialized</option>
              </select>
              
              <button
                onClick={handleCreateService}
                className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Service</span>
              </button>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {filteredServices.map((service) => (
            <div key={service.id} className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden flex flex-col h-full">
              {/* Service Header */}
              <div className="p-3 md:p-6 border-b border-gray-200 dark:border-dark-border flex-grow">
                <div className="flex justify-between items-start mb-2 md:mb-4">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary truncate">{service.name}</h3>
                  <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                    {service.popular && (
                      <span className="px-1 md:px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded-full">
                        Popular
                      </span>
                    )}
                    <button
                      onClick={() => handleToggleService(service.id)}
                      className={`p-1 md:p-2 rounded-lg transition-colors flex-shrink-0 ${
                        service.is_active 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {service.is_active ? (
                        <ToggleRight className="w-3 h-3 md:w-4 md:h-4" />
                      ) : (
                        <ToggleLeft className="w-3 h-3 md:w-4 md:h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <p className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary mb-2 md:mb-4 line-clamp-2 flex-grow">{service.description}</p>
                
                {/* Service Meta */}
                <div className="grid grid-cols-2 gap-2 md:gap-4 mb-2 md:mb-4">
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <DollarSign className="w-3 h-3 md:w-4 md:h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-dark-text-muted">Price</p>
                      <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-dark-text-primary">${service.base_price}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-dark-text-muted">Duration</p>
                      <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{service.duration_hours}h</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <span className={`px-1 md:px-2 py-1 text-xs rounded-full flex-shrink-0 ${getCategoryColor(service.category)}`}>
                      {service.category}
                    </span>
                    
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <Users className="w-2 h-2 md:w-3 md:h-3 text-gray-400 dark:text-gray-500" />
                      <span className="text-xs text-gray-600 dark:text-dark-text-secondary">
                        {service.requires_team ? `${service.min_team_size}+` : '1'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {getComplexityStars(service.complexity_level)}
                  </div>
                </div>
              </div>

              {/* Service Actions */}
              <div className="px-3 py-2 md:px-6 md:py-4 bg-gray-50 dark:bg-gray-700 flex justify-between flex-shrink-0">
                <div className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">
                  {service.is_active ? (
                    <span className="text-green-600 dark:text-green-400">● Active</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400">● Inactive</span>
                  )}
                </div>
                
                <div className="flex items-center space-x-1 md:space-x-2">
                  <button
                    onClick={() => handleViewService(service)}
                    className="p-1 md:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  >
                    <Eye className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                  <button
                    onClick={() => handleEditService(service)}
                    className="p-1 md:p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                  >
                    <Edit className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteService(service.id)}
                    className="p-1 md:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Service Modal */}
      <ServiceModal
        service={editingService}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveService}
        onDelete={handleDeleteServiceFromModal}
        mode={modalMode}
      />
    </div>
  )
}
